import {html} from 'lit';
import type {
  ImmutableResult,
  InitialProperties,
  ImmutableProgram,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  makeVariants,
  Variant,
} from '#lit/components/mixins/immutable_component/index.js';
import type {AudienceBuilderField} from '#lit/types/builders/audience_builder/index.js';
import type {Option} from '#lit/components/base/select_async/index.js';
import {truncateAndKeepWord} from '#lit/utils/strings.js';
import {when} from 'lit/directives/when.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import {MAX_DESCRIPTION_LENGTH} from '#lit/components/audience_builder/filter_addition_view/index.js';

export const FIELD_OPTION_ITEM_TAG = 'field-option-item';

export type Model = {
  option: Option<AudienceBuilderField>;
  isFullDescriptionVisible: boolean;
};

export const Msg = makeVariants({
  toggleDescription: Variant<void>()('toggle-description'),
  setOption: Variant<Option<AudienceBuilderField>>()('set-option'),
});

export type Msg = VariantsFor<typeof Msg>;

function init(props: InitialProperties): ImmutableResult<Model> {
  return {
    option: <Option<AudienceBuilderField>>props.get('option') || {
      value: null,
      label: null,
      record: {},
    },
    isFullDescriptionVisible: false,
  };
}

function update(msg: Msg, model: Model): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'toggle-description':
      return {
        ...model,
        isFullDescriptionVisible: !model.isFullDescriptionVisible,
      };
    case 'set-option':
      return {...model, option: msg.value};
  }
}

function view(model: Model, handleUpdate: (msg: Msg) => void) {
  const {option, isFullDescriptionVisible} = model;
  const truncatedDescription = truncateAndKeepWord(
    option.record.description || '',
    MAX_DESCRIPTION_LENGTH
  );
  const showMore = !!(
    truncatedDescription.length < (option.record.description || '').length
  );

  return html`
    <div class="filter-view__list-item">
      <div class="filter-view__list-item__type">
        <ab-filter-type-icon
          type=${option.record.type}
          title=${option.record.fw_type || option.record.type}
        ></ab-filter-type-icon>
      </div>
      <div class="filter-view__list-item__content">
        <div class="filter-view__list-item__content__name">
          <div class="filter-view__list-item__content__name__label">
            ${option.record.computed
              ? option.record.display_name
              : option.label}
          </div>
        </div>

        ${when(
          option.record.description,
          () => html`
            <div class="filter-view__list-item__content__description">
              ${isFullDescriptionVisible
                ? option.record.description
                : truncatedDescription}
              ${when(
                showMore && !isFullDescriptionVisible,
                () => html`
                  <div
                    class="filter-view__list-item__content__description__more"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      handleUpdate(Msg.toggleDescription());
                    }}
                  >
                    ...
                  </div>
                `
              )}
            </div>
          `
        )}
      </div>
      ${when(
        option.record.computed,
        () =>
          html` <div class="filter-view__list-item__computed">
            <fly-icon icon="dashboard"></fly-icon>
          </div>`
      )}
    </div>
  `;
}

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
